import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "../services/api";
import { useSocketStore } from "./socket";

// Debounce + in-flight dedupe caps the REST "thundering herd" described in
// Tasks/12-scalability-and-bottleneck-hardening.md (Phase 0 / TODO 0.2):
// every open admin tab used to independently call GET /api/votes/event/:id
// on every single vote_updated/song_performed/song_ended broadcast. Mirrors
// the concerts store implementation (web/concerts/src/stores/votes.js).
const REFRESH_DEBOUNCE_MS = 1500;
const REFRESH_JITTER_MS = 500;
// Ceiling on how long a continuous stream of broadcasts may keep postponing a
// refresh. A pure trailing debounce starves: during the busiest moment of a
// show, broadcasts arrive closer together than the debounce window, so the
// timer is reset forever and the refresh never fires — exactly when the data
// is changing fastest.
const REFRESH_MAX_WAIT_MS = 4000;

// Axios reports an aborted request through one of these; they are expected
// during teardown and must not be logged as failures.
const isAbortError = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";

const createAbortController = () =>
  typeof AbortController === "undefined" ? null : new AbortController();

// GET /api/votes/event/:id is `public, max-age=2, stale-while-revalidate=10`.
// That is right for audience phones and wrong for this operator panel: Play
// next ends the current song and starts the next one inside that window, so
// the follow-up GET can be served the in-between snapshot (previous song
// ended, next song still only "up next"). The panel then shows Start set
// while the set timer keeps running, until a reload happens to miss the
// cache. A unique query param misses every cache layer without a
// Cache-Control request header, which would be a cross-origin preflight.
// A counter, not Date.now(), so two reads in the same millisecond — end song,
// then start the next — cannot share a cache entry.
let cacheBust = 0;
const bypassHttpCache = (baseConfig) => ({
  ...baseConfig,
  params: { ...baseConfig?.params, _fresh: ++cacheBust }
});

export const useVotesStore = defineStore("votes", () => {
  const votes = ref({});
  const userVotes = ref({});
  const upNextSongIds = ref({});
  const queueEntries = ref({});
  const nowPlaying = ref({});
  const performedHistory = ref({});
  const isLoading = ref(false);
  const currentEventId = ref(null);
  // Last-seen monotonic broadcast sequence per event. A delayed broadcast
  // (song_ended is emitted before song_performed during Play next) must not
  // rewind the panel, and a gap or seq-source change schedules one resync.
  const lastSeqByEvent = ref({});
  const lastSeqSourceByEvent = ref({});

  // In-flight request de-duplication: concurrent callers for the same eventId
  // share one network request instead of firing one each.
  const inFlightFetches = new Map(); // eventId -> { promise, controller }
  // Trailing-debounce bookkeeping for socket-triggered refreshes.
  const pendingRefreshTimers = new Map(); // eventId -> { timer, firstScheduledAt }
  // Guards against an older, slower response overwriting newer state.
  const lastAppliedSequence = new Map(); // eventId -> number
  let fetchSequence = 0;

  const getEventVotes = computed(() => (eventId) => {
    return votes.value[eventId] || [];
  });

  const getQueueEntries = computed(() => (eventId) => {
    return queueEntries.value[eventId] || [];
  });

  const getNowPlaying = computed(() => (eventId) => {
    return nowPlaying.value[eventId] || null;
  });

  const getPerformedHistory = computed(() => (eventId) => {
    return performedHistory.value[eventId] || [];
  });

  const getUserVotesForEvent = computed(() => (eventId) => {
    return userVotes.value[eventId] || [];
  });

  const getSongVoteCount = computed(() => (eventId, songId) => {
    const eventVotes = votes.value[eventId] || [];
    const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
    return songVote ? songVote.totalVotes : 0;
  });

  const hasUserVotedForSong = computed(() => (eventId, songId) => {
    const userEventVotes = userVotes.value[eventId] || [];
    return userEventVotes.some((vote) => vote.songId === songId);
  });

  const getUpNextSongId = computed(() => (eventId) => {
    return upNextSongIds.value[eventId] || null;
  });

  const applyEventVotePayload = (eventId, data) => {
    if (Array.isArray(data.queueEntries)) {
      queueEntries.value[eventId] = data.queueEntries;
      nowPlaying.value[eventId] = data.nowPlaying || null;
      performedHistory.value[eventId] = data.performedHistory || [];
      votes.value[eventId] = [];
      upNextSongIds.value[eventId] = null;
      return;
    }

    votes.value[eventId] = data.results || [];
    upNextSongIds.value[eventId] = data.upNextSongId || null;
    queueEntries.value[eventId] = [];
    nowPlaying.value[eventId] = null;
    performedHistory.value[eventId] = [];
  };

  // True when a realtime payload carries usable vote/queue state inline
  // (TODO 1.4/1.5), so the caller can apply it directly instead of
  // scheduling a REST refetch. Concert payloads carry `results` (voters
  // stripped — see TODO 1.4), karaoke payloads carry `queueEntries`.
  const hasEnrichedPayload = (data) =>
    Boolean(data) && (Array.isArray(data.queueEntries) || Array.isArray(data.results));

  // "ok" — apply the payload.
  // "resync" — apply it, and also refetch; a seq was skipped or the counter
  // source changed (process restart / in-memory fallback).
  // "stale" — seq went backwards on the same source. This is a delayed older
  // broadcast, not a restart. Do not apply it and do not move the cursor.
  // "unsequenced" — payload has no seq; keep the legacy apply-or-refetch path.
  const trackSeq = (eventId, seq, seqSource) => {
    if (eventId === null || eventId === undefined || typeof seq !== "number") {
      return "unsequenced";
    }

    const prev = lastSeqByEvent.value[eventId];
    const prevSource = lastSeqSourceByEvent.value[eventId];
    const nextSource = typeof seqSource === "string" ? seqSource : undefined;

    if (prev !== undefined && prevSource === nextSource && seq < prev) {
      return "stale";
    }

    lastSeqByEvent.value[eventId] = seq;
    if (nextSource !== undefined) {
      lastSeqSourceByEvent.value[eventId] = nextSource;
    } else if (prevSource !== undefined) {
      delete lastSeqSourceByEvent.value[eventId];
    }

    if (prev === undefined) {
      return "ok";
    }
    if (prevSource !== nextSource) {
      return "resync";
    }
    if (seq > prev + 1) {
      return "resync";
    }
    return "ok";
  };

  // Shared tail of every realtime handler below (TODO 1.4/1.5/1.6): apply an
  // enriched payload inline, or fall back to the debounced REST refetch so
  // older/degraded broadcasts still resync eventually.
  //
  // TODO 2.5.2: the monotonic guard in fetchEventVotesNow only ordered REST
  // responses *against each other* — an inline apply here never touched
  // lastAppliedSequence, so a REST request issued before this inline apply
  // (T1), which itself landed after an even newer broadcast was applied
  // inline (T2 > T1), could still resolve at T3 and pass the sequence check,
  // clobbering the newer socket state with pre-broadcast data. Claiming a
  // ticket here — the same counter fetchEventVotesNow uses — makes any
  // older in-flight REST response fail that check instead.
  //
  // A seq gap or source change still applies the payload we have, then
  // schedules one refetch so a missed intermediate broadcast is recovered.
  const applyPayloadOrScheduleRefresh = (eventId, data, { needsResync = false } = {}) => {
    if (hasEnrichedPayload(data)) {
      lastAppliedSequence.set(eventId, ++fetchSequence);
      applyEventVotePayload(eventId, data);
      if (needsResync) {
        scheduleVotesRefresh(eventId);
      }
    } else {
      scheduleVotesRefresh(eventId);
    }
  };

  const submitVote = async (eventId, songId, options = {}) => {
    try {
      isLoading.value = true;

      const payload = { eventId, songId };
      if (options.performerName) {
        payload.performerName = options.performerName;
      }

      const response = await api.post("/api/votes", payload);

      if (response.data.success) {
        // Forced: a request already in flight was issued before this write, so
        // joining it would render pre-write data.
        await refreshEventVotes(eventId);
        await fetchUserVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("submit_vote", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to submit vote"
      };
    } finally {
      isLoading.value = false;
    }
  };

  // The real network call, shared by the immediate and debounced paths.
  // Concurrent callers for the same eventId collapse into a single in-flight
  // request rather than issuing one per caller.
  //
  // `force` starts a fresh request even when one is already in flight. It is
  // required after a local write: the in-flight response was produced by a
  // query that ran *before* the write, so reusing it would show the operator
  // pre-write data (e.g. a song still listed as unperformed immediately after
  // they marked it performed).
  const fetchEventVotesNow = (eventId, { force = false } = {}) => {
    if (eventId === null || eventId === undefined || eventId === "") {
      return Promise.resolve();
    }

    const existing = inFlightFetches.get(eventId);
    if (existing && !force) {
      return existing.promise;
    }
    if (existing) {
      existing.controller?.abort();
      inFlightFetches.delete(eventId);
    }

    const sequence = ++fetchSequence;
    const controller = createAbortController();
    const entry = { controller };

    entry.promise = (async () => {
      try {
        const response = await api.get(
          `/api/votes/event/${eventId}`,
          bypassHttpCache(controller ? { signal: controller.signal } : undefined)
        );

        // Drop out-of-order responses so a slow earlier request can never
        // clobber state a later one already applied.
        if (sequence < (lastAppliedSequence.get(eventId) || 0)) {
          return;
        }

        if (response.data.success) {
          lastAppliedSequence.set(eventId, sequence);
          applyEventVotePayload(eventId, response.data.data);
          currentEventId.value = eventId;
        }
      } catch (error) {
        if (!isAbortError(error)) {
          console.error("Failed to fetch event votes:", error);
        }
      } finally {
        // Only release the slot if it is still ours — a forced fetch may have
        // already replaced it, and blindly deleting would evict that newer
        // entry and silently disable de-duplication.
        if (inFlightFetches.get(eventId) === entry) {
          inFlightFetches.delete(eventId);
        }
      }
    })();

    inFlightFetches.set(eventId, entry);
    return entry.promise;
  };

  // Kept as the default export name so existing direct call sites (initial
  // mount load) are unaffected and stay immediate.
  const fetchEventVotes = (eventId) => fetchEventVotesNow(eventId);

  // Use after a local write, when joining an in-flight (pre-write) request
  // would return stale data.
  const refreshEventVotes = (eventId) => fetchEventVotesNow(eventId, { force: true });

  // Trailing debounce (with jitter) used exclusively by high-frequency socket
  // handlers below, so a burst of broadcasts for one event collapses into a
  // single request instead of one per broadcast. Jitter de-synchronises
  // concurrently open admin tabs so they don't all refetch at once, and
  // REFRESH_MAX_WAIT_MS guarantees the refresh still happens under a
  // sustained broadcast stream.
  const scheduleVotesRefresh = (eventId) => {
    if (eventId === null || eventId === undefined || eventId === "") {
      return;
    }

    const now = Date.now();
    const existing = pendingRefreshTimers.get(eventId);
    const firstScheduledAt = existing ? existing.firstScheduledAt : now;

    if (existing) {
      clearTimeout(existing.timer);
    }

    const remainingCap = Math.max(0, REFRESH_MAX_WAIT_MS - (now - firstScheduledAt));
    const delay = Math.min(REFRESH_DEBOUNCE_MS + Math.random() * REFRESH_JITTER_MS, remainingCap);

    const timer = setTimeout(() => {
      pendingRefreshTimers.delete(eventId);
      fetchEventVotesNow(eventId);
    }, delay);

    pendingRefreshTimers.set(eventId, { timer, firstScheduledAt });
  };

  // Cancels any pending debounced refresh and aborts an in-flight request for
  // an event, so navigating away doesn't leak timers or write a response into
  // state belonging to a view that has already unmounted.
  const cancelPendingRefresh = (eventId) => {
    const pending = pendingRefreshTimers.get(eventId);
    if (pending) {
      clearTimeout(pending.timer);
      pendingRefreshTimers.delete(eventId);
    }

    const inFlight = inFlightFetches.get(eventId);
    if (inFlight) {
      // Abort rather than merely forgetting the entry: dropping the map entry
      // on its own leaves the request running and its result still lands in
      // the store.
      inFlight.controller?.abort();
      inFlightFetches.delete(eventId);
    }
  };

  const cancelAllPendingRefreshes = () => {
    pendingRefreshTimers.forEach((pending) => clearTimeout(pending.timer));
    pendingRefreshTimers.clear();
    inFlightFetches.forEach((entry) => entry.controller?.abort());
    inFlightFetches.clear();
    lastAppliedSequence.clear();
  };

  const fetchUserVotes = async (eventId) => {
    try {
      const deviceId = localStorage.getItem("deviceId");
      if (deviceId) {
        const response = await api.get(`/api/votes/device/${eventId}`);
        if (response.data.success) {
          userVotes.value[eventId] = response.data.data.votes || [];
        }
      } else {
        userVotes.value[eventId] = [];
      }
    } catch (error) {
      console.error("Failed to fetch user votes:", error);
      userVotes.value[eventId] = [];
    }
  };

  const markAsPerformed = async (eventId, songId, options = {}) => {
    try {
      const payload = {
        eventId,
        songId,
        skipAutoQueue: options.skipAutoQueue || false
      };
      if (options.voteId) {
        payload.voteId = options.voteId;
      }

      const response = await api.post("/api/votes/performed", payload);

      if (response.data.success) {
        // Forced: joining a pre-write in-flight request would leave the song
        // still showing as unperformed.
        await refreshEventVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("mark_performed", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to mark song as performed"
      };
    }
  };

  const setEndTime = async (eventId, songId, options = {}) => {
    try {
      const payload = { eventId, songId };
      if (options.performedSongId) {
        payload.performedSongId = options.performedSongId;
      }
      if (options.endSet) {
        payload.endSet = true;
      }

      const response = await api.post("/api/votes/performed/end", payload);

      if (response.data.success) {
        // Forced: see markAsPerformed.
        await refreshEventVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("song_ended", { eventId, songId });

        return { success: true, data: response.data.data };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to set song end time"
      };
    }
  };

  const clearUpNext = async (eventId) => {
    try {
      const response = await api.post("/api/votes/clear-up-next", { eventId });

      if (response.data.success) {
        upNextSongIds.value[eventId] = null;

        const socketStore = useSocketStore();
        socketStore.emit("song_performed", { eventId, upNextSongId: null });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to unlock up next song"
      };
    }
  };

  const reorderKaraokeQueue = async (eventId, voteIds) => {
    const previous = queueEntries.value[eventId] || [];
    const order = new Map(voteIds.map((id, index) => [id, index]));
    const optimistic = [...previous].sort((a, b) => order.get(a.voteId) - order.get(b.voteId));
    queueEntries.value[eventId] = optimistic;

    try {
      const response = await api.post("/api/votes/karaoke/reorder", { eventId, voteIds });

      if (response.data.success) {
        // Other connected clients are notified via the server-pushed "vote_updated"
        // socket event (see votesStore.handleVoteUpdated); no client emit needed here.
        return { success: true };
      }
    } catch (error) {
      queueEntries.value[eventId] = previous;
      // Forced: this is a rollback, so it must observe server truth rather
      // than reuse a response that may predate the failed reorder.
      await refreshEventVotes(eventId);

      return {
        success: false,
        message: error.response?.data?.message || "Failed to reorder karaoke queue"
      };
    }
  };

  const startNextSet = async (eventId, extraDuration = undefined) => {
    try {
      const payload = { eventId };
      if (extraDuration !== undefined) payload.extraDuration = extraDuration;
      const response = await api.post("/api/votes/start-next-set", payload);
      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data.message || "Failed to start next set" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to start next set"
      };
    }
  };

  const setSetEndSongCount = async (eventId, count) => {
    try {
      const response = await api.post("/api/votes/set-end-count", { eventId, count });
      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || "Failed to set set-end song count"
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to set set-end song count"
      };
    }
  };

  const dispatchRealtime = (eventId, data, seq, seqSource, beforeApply) => {
    const freshness = trackSeq(eventId, seq, seqSource);
    if (freshness === "stale") {
      // Delayed older broadcast (Play next emits song_ended before
      // song_performed). Applying it would drop the song that just started
      // and leave the panel on Start set + Up next.
      return;
    }
    if (beforeApply) {
      beforeApply();
    }
    applyPayloadOrScheduleRefresh(eventId, data, { needsResync: freshness === "resync" });
  };

  const handleVoteUpdated = (data) => {
    const { eventId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    dispatchRealtime(eventId, data, seq, seqSource);
  };

  const handleSongPerformed = (data) => {
    const { eventId, upNextSongId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    dispatchRealtime(eventId, data, seq, seqSource, () => {
      // Update upNextSongId immediately from the socket event, in case the
      // enriched payload below doesn't carry one (e.g. karaoke).
      if (upNextSongId !== undefined) {
        upNextSongIds.value[eventId] = upNextSongId || null;
      }
    });
  };

  const handleSongEnded = (data) => {
    const { eventId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    dispatchRealtime(eventId, data, seq, seqSource);
  };

  // set_changed draws from the same per-event seq counter. If the store never
  // sees it, the next broadcast looks like a gap and triggers a needless refetch.
  const handleSetChanged = (data) => {
    const { eventId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    dispatchRealtime(eventId, data, seq, seqSource);
  };

  const clearVotes = () => {
    // Drop queued/in-flight refreshes first, otherwise a debounced refetch
    // scheduled moments earlier lands after the reset and repopulates state.
    cancelAllPendingRefreshes();
    votes.value = {};
    userVotes.value = {};
    queueEntries.value = {};
    nowPlaying.value = {};
    performedHistory.value = {};
    currentEventId.value = null;
    lastSeqByEvent.value = {};
    lastSeqSourceByEvent.value = {};
  };

  return {
    votes,
    userVotes,
    upNextSongIds,
    queueEntries,
    nowPlaying,
    performedHistory,
    isLoading,
    currentEventId,
    lastSeqByEvent,
    getEventVotes,
    getQueueEntries,
    getNowPlaying,
    getPerformedHistory,
    getUserVotesForEvent,
    getSongVoteCount,
    hasUserVotedForSong,
    getUpNextSongId,
    submitVote,
    fetchEventVotes,
    fetchEventVotesNow,
    fetchUserVotes,
    cancelPendingRefresh,
    markAsPerformed,
    setEndTime,
    clearUpNext,
    startNextSet,
    setSetEndSongCount,
    reorderKaraokeQueue,
    handleVoteUpdated,
    handleSongPerformed,
    handleSongEnded,
    handleSetChanged,
    clearVotes
  };
});
